import { useEffect, useState } from "react";
import { LockKeyhole, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Otp } from "@/lib/api";

const errMsg = (e, fb) => e?.response?.data?.detail?.message_en || fb;

/** Purpose-bound inline OTP verification. Calls onVerified(verification_token) on success. */
export const OtpVerifyInline = ({ purpose, channel, identity, onVerified, testId = "otp-inline" }) => {
  const [stage, setStage] = useState("idle"); // idle | sent | verified
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [masked, setMasked] = useState("");
  useEffect(() => { if (cooldown <= 0) return undefined; const t = setTimeout(() => setCooldown((c) => c - 1), 1000); return () => clearTimeout(t); }, [cooldown]);
  useEffect(() => { setStage("idle"); setOtp(""); }, [identity, channel]);
  const valid = channel === "sms" ? /^[6-9]\d{9}$/.test(identity || "") : /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(identity || "");

  const send = async () => {
    setBusy(true);
    try {
      const d = await Otp.request({ purpose, channel, ...(channel === "sms" ? { mobile: identity } : { email: identity }) });
      setMasked(d.identity_masked); setCooldown(d.resend_after_seconds || 60); setStage("sent");
      toast.success(`OTP sent to ${d.identity_masked}`);
    } catch (e) { toast.error(errMsg(e, "Could not send OTP")); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const d = await Otp.verify({ purpose, channel, otp, ...(channel === "sms" ? { mobile: identity } : { email: identity }) });
      setStage("verified"); onVerified?.(d.verification_token);
      toast.success("Verified");
    } catch (e) { toast.error(errMsg(e, "Verification failed"), { description: e?.response?.data?.detail?.attempts_left != null ? `${e.response.data.detail.attempts_left} attempt(s) left` : undefined }); }
    finally { setBusy(false); }
  };

  if (stage === "verified") {
    return <div data-testid={`${testId}-verified`} className="inline-flex items-center gap-2 text-emerald-300 text-sm"><ShieldCheck size={15} /> {channel === "sms" ? "Mobile" : "Email"} verified ({masked})</div>;
  }
  return (
    <div data-testid={testId} className="rounded-xl border border-[#D4AF37]/25 bg-white/[0.03] p-4 space-y-3">
      <div className="flex items-center gap-2 text-white/85 text-sm"><LockKeyhole size={15} className="text-[#D4AF37]" /> Verify your {channel === "sms" ? "mobile number" : "email address"}</div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" data-testid={`${testId}-send`} onClick={send} disabled={!valid || busy || cooldown > 0}
          className="min-h-[40px] px-4 rounded-full border border-[#D4AF37]/50 text-[#F0D788] text-sm hover:bg-[#D4AF37]/10 disabled:opacity-40">
          {busy && stage === "idle" ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : stage === "sent" ? "Resend OTP" : "Send OTP"}
        </button>
        {stage === "sent" && (
          <>
            <input data-testid={`${testId}-input`} inputMode="numeric" maxLength={6} value={otp} placeholder="6-digit OTP"
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="min-h-[40px] w-36 rounded-full bg-[#0A1F44] border border-white/15 focus:border-[#D4AF37] outline-none px-4 text-white text-sm tracking-[0.3em] text-center" />
            <button type="button" data-testid={`${testId}-verify`} onClick={verify} disabled={otp.length !== 6 || busy}
              className="min-h-[40px] px-4 rounded-full btn-gold text-sm font-semibold disabled:opacity-40">Verify</button>
          </>
        )}
      </div>
      {!valid && <p className="text-[11px] text-white/50">Enter a valid {channel === "sms" ? "10-digit mobile" : "email"} above to receive the OTP.</p>}
    </div>
  );
};
